import { z } from 'zod';
import { TOURNAMENT_STATUSES, TOURNAMENT_TYPES } from '../constants/index.js';
import { characterSchema } from './character.js';
/**
 * 大会（企画書 §17、[ADR-023](../../../../docs/adr/023-tournament-progression.md)）。
 *
 * 型ではなく **zod スキーマが仕様の正**（[ADR-005](../../../../docs/adr/005-zod-in-shared-package.md)）。
 * iOS も同じ形を読むので、変えるときは `packages/contracts/mobile/` の JSON も合わせる。
 */
export const tournamentTypeSchema = z.enum(TOURNAMENT_TYPES);
export const tournamentStatusSchema = z.enum(TOURNAMENT_STATUSES);
export const tournamentSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: tournamentTypeSchema,
    status: tournamentStatusSchema,
    /** 参加枠。MVP は 8。 */
    maxEntries: z.number().int().positive(),
    /** 決勝までのラウンド数。8 人なら 3。 */
    totalRounds: z.number().int().positive(),
    /** 受付の締切。過ぎたら残りの枠を CPU で埋めて始める。 */
    entryClosesAt: z.string().min(1),
    /** `RUNNING` になった時刻。受付中は無い。 */
    startedAt: z.string().min(1).optional(),
    /** 優勝が決まった時刻。終わるまで無い。 */
    finishedAt: z.string().min(1).optional(),
    createdAt: z.string().min(1),
});
/**
 * 大会への参加と、その成績。
 *
 * `points` は**負けた時点で確定する**（優勝なら決勝に勝った時点）。ランキングはこれを日付範囲で
 * 合計する（ADR-012 / ADR-013）。進行中は `reachedRound` も `points` も無い。
 */
export const tournamentEntrySchema = z.object({
    tournamentId: z.string().min(1),
    characterId: z.string().min(1),
    /** ブラケット上の位置（1..`maxEntries`）。第 1 シードが 1。 */
    seedPosition: z.number().int().positive(),
    /** 最後に戦ったラウンド。進行中は無い。 */
    reachedRound: z.number().int().positive().optional(),
    /** 確定した順位ポイント。進行中は無い。 */
    points: z.number().int().nonnegative().optional(),
    enteredAt: z.string().min(1),
});
/**
 * ブラケットの 1 枠。
 *
 * **戦う前から存在する。** 大会が始まった時点で全ラウンドぶんの枠が作られ、1 回戦だけ両者が
 * 埋まっている。勝ち上がりが決まると次のラウンドの枠に書き込まれる（ADR-023）。
 */
export const tournamentMatchSchema = z.object({
    id: z.string().min(1),
    tournamentId: z.string().min(1),
    /** 1 = 1 回戦。 */
    round: z.number().int().positive(),
    /** そのラウンド内の位置（0 起点）。勝者は次のラウンドの `floor(position / 2)` へ進む。 */
    position: z.number().int().nonnegative(),
    /** 勝ち上がりが決まるまで無い。 */
    characterAId: z.string().min(1).optional(),
    characterBId: z.string().min(1).optional(),
    /**
     * 次へ進む側。
     *
     * **引き分けでも必ずどちらかが入る。** `Battle.winner` には `DRAW` がそのまま入っており、
     * 「トーナメントで誰が上がったか」はこちらが持つ。2 つを混ぜない（ADR-023）。
     */
    winnerId: z.string().min(1).optional(),
    /** 実際に戦った対戦。リプレイはこれを引く。戦う前は無い。 */
    battleId: z.string().min(1).optional(),
    /** この試合を回してよくなる時刻。 */
    scheduledAt: z.string().min(1),
});
/**
 * 大会の詳細（ブラケット表示に必要なものが揃っている）。
 *
 * 選手の名前と絵は `characters` を引き直さずに済むよう、参加者を一緒に返す。**MVP は自分以外
 * すべて CPU** なので、他人の名前や絵が出ることは無い（ADR-023）。
 */
export const tournamentDetailSchema = tournamentSchema.extend({
    entries: z.array(tournamentEntrySchema),
    matches: z.array(tournamentMatchSchema),
    /**
     * 参加している選手そのもの。**ブラケットに出てくる ID の引き当て先。**
     *
     * これが無いと、クライアントが枠の数だけ選手を取りに行くことになる（8 人なら 8 回）。
     * CPUカップは自分以外すべて CPU。**ワールドカップでは他の人の選手が入る**（ADR-032。非公開の選手は伏せる。ADR-031）。
     */
    characters: z.array(characterSchema),
});
/** 大会に出る（`POST /tournaments`）。**選手の ID と大会の種類だけ**を受ける（企画書 §40-7）。 */
export const enterTournamentSchema = z.object({
    characterId: z.string().min(1),
    /**
     * 出る大会（#254、ADR-032）。**無ければ CPUカップ**（この項目より前のアプリはこれを送らない）。
     * `REGIONAL` / `NATIONAL` は作らないので受け付けない。
     */
    type: z.enum(['CPU', 'WORLD']).optional(),
});
/**
 * ワールドカップが開いているか（`GET /api/v1/tournaments/world-cup`、#277）。アプリは「大会に出る」の画面を開いたときに読み、
 * 閉じていればワールドカップを「じゅんび ちゅう」にする（#255）。閉じていると `POST /tournaments` は 403 `WORLD_CUP_UNAVAILABLE`。
 */
export const worldCupAvailabilitySchema = z.object({
    available: z.boolean(),
});
